import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, Download, Filter, Footprints, ImageOff, IndianRupee, MapPin, Plus, ShieldAlert, ShieldCheck, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { StaffAvatar, StaffName, StaffLabel, staffLabelText } from "../../components/StaffAvatar";
import { RoleBadge, VISIT_OUTCOME_META, outcomeLabel, ReceiptDeletedBadge } from "../../components/VisitTimeline";
import { AuthedImg, GpsPhotoDialog } from "../../components/GpsPhotoGallery";
import { useTerritoryMasters, TerritoryFilterSelects, namesOf } from "../../components/TerritoryFilter";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate, staffInTerritory } from "../../components/FetchBar";
import { PropertyDetailSheet } from "./Properties";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg, fmtINR, fmtDate, fmtDateTime, downloadBlob, istDateISO } from "../../lib/api";
import { firstAllowedTab, useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel, ReadOnlyNotice, ViewOnlyBadge } from "../../components/FeatureGate";

// Phase 97: the report's business day is the IST calendar day (not the device/UTC date)
const todayISO = () => istDateISO();
const daysAgoISO = (n) => istDateISO(new Date(Date.now() - n * 86400000));

/** IST date + clock of a visit — from the stored IST fields, falling back to converting created_at. */
function VisitWhen({ v, testId }) {
  const date = v.visit_date_ist || (v.created_at ? istDateISO(new Date(v.created_at)) : null);
  const time = v.visit_time_ist ? v.visit_time_ist.slice(0, 5)
    : v.created_at ? new Date(v.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) : null;
  return (
    <span data-testid={testId} title={v.visited_at_ist ? `${v.visited_at_ist} (IST)` : fmtDateTime(v.created_at)}>
      {date ? fmtDate(date) : "-"}
      <p className="text-muted-foreground tabular-nums" data-testid={testId ? `${testId}-time` : undefined}>
        {time || "-"}<span className="ml-1 text-[10px] uppercase tracking-wide">IST</span>
      </p>
    </span>
  );
}

/**
 * Phase 97: inline GPS photo for a visit row — the latest photo of the property taken on the
 * visit's IST day, with a +N badge when there are more. Click → full photo + GPS map, and a strip
 * to flip through the other same-day photos.
 */
function VisitPhotoCell({ code, v, index }) {
  const [active, setActive] = useState(null);
  const photos = v.photos || [];
  if (photos.length === 0) {
    return <span className="inline-flex items-center gap-1 text-muted-foreground text-xs" data-testid={`visit-report-photo-none-${index}`}><ImageOff className="h-3.5 w-3.5" /> -</span>;
  }
  const first = photos[0];
  return (
    <>
      <span className="relative inline-block align-middle">
        <button type="button" onClick={() => setActive(first)}
          className="group relative block rounded-md overflow-hidden border focus-visible:ring-2 focus-visible:ring-ring"
          title={`${photos.length} GPS photo${photos.length === 1 ? "" : "s"} taken on this day — click to view`}
          data-testid={`visit-report-photo-${index}`} data-photo-count={photos.length}>
          <AuthedImg code={code} fileId={first.file_id} alt={first.filename || "GPS photo"}
            className="h-11 w-16 object-cover cursor-pointer transition-transform duration-200 group-hover:scale-105" />
          <span className={`absolute bottom-0 left-0 right-0 flex items-center justify-center gap-0.5 text-[9px] py-px ${first.lat != null ? "bg-emerald-700/85 text-white" : "bg-amber-600/85 text-white"}`}>
            <MapPin className="h-2.5 w-2.5" /> {first.lat != null ? "GPS" : "No GPS"}
          </span>
        </button>
        {photos.length > 1 && (
          <Badge className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 text-[9px] leading-none rounded-full pointer-events-none" data-testid={`visit-report-photo-more-${index}`}>
            +{photos.length - 1}
          </Badge>
        )}
      </span>
      <GpsPhotoDialog code={code} photo={active} onClose={() => setActive(null)}
        extra={(
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground" data-testid="visit-photo-context">
              <span className="font-mono text-foreground">{v.pin}</span> • {v.owner_name || "-"} • visit on {fmtDate(v.visit_date_ist)} at {(v.visit_time_ist || "").slice(0, 5)} IST by {staffLabelText(v.visited_by_name, v.visited_by_employee_id)}
            </p>
            {photos.length > 1 && (
              <div className="flex flex-wrap gap-1.5" data-testid="visit-photo-strip">
                {photos.map((p) => (
                  <button key={p.file_id} type="button" onClick={() => setActive(p)}
                    className={`rounded-md overflow-hidden border-2 ${active?.file_id === p.file_id ? "border-primary" : "border-transparent hover:border-muted-foreground/40"}`}
                    data-testid={`visit-photo-strip-${p.file_id}`}>
                    <AuthedImg code={code} fileId={p.file_id} alt={p.filename || "photo"} className="h-12 w-16 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )} />
    </>
  );
}

function SummaryCard({ label, value, sub, icon: Icon, testId }) {
  return (
    <Card className="shadow-sm">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <Icon className="h-4.5 w-4.5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-lg font-semibold tabular-nums leading-tight" data-testid={testId}>{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function ReportTab({ code, isAdmin, readOnly = false }) {
  const [filters, setFilters] = useState({
    date_from: daysAgoISO(30), date_to: todayISO(), visited_by_role: "", outcome: "", staff_id: "", search: "",
    zone_id: "", ward_id: "", mohalla_id: "",   // Phase 98: territory (cascading Zone -> Ward -> Mohalla)
  });
  const territoryMasters = useTerritoryMasters(code);
  const [data, setData] = useState(null);
  // Phase 115: staff grouped by role (scope-aware) — feeds the smart "Staff" dropdown
  const [staffByRole, setStaffByRole] = useState({ supervisor: [], tax_collector: [], municipal_admin: [] });
  const [exporting, setExporting] = useState(false);
  const [focusProp, setFocusProp] = useState(null);

  useEffect(() => {
    api.get(`/t/${code}/visits/report/staff-options`)
      .then((r) => setStaffByRole({ supervisor: [], tax_collector: [], municipal_admin: [], ...(r.data || {}) }))
      .catch(() => {});
  }, [code]);

  // Staff list follows Zone / Ward (Phase 117: only people mapped there — supervisors via zones,
  // collectors via wards) and the "Visited by" role: one role => that role's staff; "All roles" => everyone, grouped.
  const ROLE_LABELS = { supervisor: "Supervisors", tax_collector: "Tax Collectors", municipal_admin: "Admins" };
  const roleOrder = isAdmin ? ["supervisor", "tax_collector", "municipal_admin"] : ["supervisor", "tax_collector"];
  // Ward is the finer filter, so when both are set the ward decides (a supervisor's ward list is derived from their zones).
  // Phase 176: zoneId / wardId may be comma-joined lists — any selected id matches (shared helper from FetchBar).
  const inTerritory = useCallback((u, zoneId, wardId) => staffInTerritory(u, zoneId, wardId), []);
  const staffGroups = useMemo(() => {
    const roles = filters.visited_by_role ? [filters.visited_by_role] : roleOrder;
    return roles.map((r) => ({
      role: r, label: ROLE_LABELS[r],
      items: (staffByRole[r] || []).filter((u) => inTerritory(u, filters.zone_id, filters.ward_id)),
    })).filter((g) => g.items.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.visited_by_role, filters.zone_id, filters.ward_id, staffByRole, isAdmin]);
  const staffCount = staffGroups.reduce((n, g) => n + g.items.length, 0);
  const staffOptionLabel = (u) => `${staffLabelText(u.name, u.employee_id)}${u.is_active === false ? " (inactive)" : ""}`;
  const territoryLabel = filters.ward_id
    ? (namesOf(filters.ward_id, territoryMasters.wards, "ward_name", "Ward") || "this ward")
    : filters.zone_id ? (namesOf(filters.zone_id, territoryMasters.zones, "zone_name", "Zone") || "this zone") : "";
  /** Is `staffId` still offered for the given role + territory? (used to drop a stale selection) */
  const staffStillListed = (staffId, role, zoneId, wardId) => {
    if (!staffId) return true;
    const roles = role ? [role] : roleOrder;
    return roles.some((r) => (staffByRole[r] || []).some((u) => u.id === staffId && inTerritory(u, zoneId, wardId)));
  };
  const changeRole = (v) => {
    const role = v === "all" ? "" : v;
    // keep the chosen staff member only if they belong to the newly selected role (and territory)
    const keep = staffStillListed(filters.staff_id, role, filters.zone_id, filters.ward_id);
    setFilters({ ...filters, visited_by_role: role, staff_id: keep ? filters.staff_id : "" });
  };
  const changeTerritory = (t) => {
    // Zone / Ward changed -> the Staff list narrows; a person no longer mapped there falls back to "All staff"
    const keep = staffStillListed(filters.staff_id, filters.visited_by_role, t.zoneId, t.wardId);
    setFilters({ ...filters, zone_id: t.zoneId, ward_id: t.wardId, mohalla_id: t.mohallaId, staff_id: keep ? filters.staff_id : "" });
  };
  const changeStaff = (v) => setFilters({ ...filters, staff_id: v === "all" ? "" : v });
  // Staff mapping arrives after the first render — if the URL/prior state names someone outside the list, reset it.
  useEffect(() => {
    if (filters.staff_id && !staffStillListed(filters.staff_id, filters.visited_by_role, filters.zone_id, filters.ward_id)) {
      setFilters((f) => ({ ...f, staff_id: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffByRole]);

  // Phase 133: the report runs only when the user clicks Fetch, on the filters applied at that moment
  const gate = useFetchGate(filters);
  // Page navigation: the summary cards always cover the whole range; the table shows one page of rows
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Fetch always starts from page 1 (state updates are batched with the gate's own, so only one request goes out)
  const runFetch = useCallback(() => { setPage(1); gate.fetch(); }, [gate.fetch]);   // eslint-disable-line react-hooks/exhaustive-deps
  const changePageSize = (n) => { setPageSize(n); setPage(1); };
  const params = useMemo(() => {
    const p = {};
    Object.entries(gate.applied || {}).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [gate.appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [loading, setLoading] = useState(false);

  // Latest-wins loading: ignore responses from superseded requests (an older, slower response must not overwrite newer rows)
  const reqSeq = React.useRef(0);
  const load = useCallback(() => {
    const seq = ++reqSeq.current;
    setLoading(true);
    return api.get(`/t/${code}/visits/report`, { params: { ...params, page, limit: pageSize } })
      .then((r) => { if (seq === reqSeq.current) setData(r.data); })
      .catch((e) => { if (seq === reqSeq.current) toast.error(errMsg(e)); })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  }, [code, params, page, pageSize]);
  useEffect(() => { if (gate.fetched) load(); }, [load, gate.fetched, gate.version]);

  const doExport = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(params).toString();
      await downloadBlob(`/t/${code}/visits/report/export?${qs}`,
        `visit-report_${filters.date_from || "all"}_to_${filters.date_to || "today"}.xlsx`);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const s = data?.summary;
  const total = data?.total ?? data?.rows?.length ?? 0;
  const pages = data?.pages || 1;
  const curPage = data?.page || page;
  const firstRow = total === 0 ? 0 : (curPage - 1) * (data?.page_size || pageSize) + 1;
  const lastRow = total === 0 ? 0 : firstRow + (data?.rows?.length || 0) - 1;
  const resetFilters = () => setFilters({ ...filters, zone_id: "", ward_id: "", mohalla_id: "", visited_by_role: "", staff_id: "", outcome: "", search: "" });
  return (
    <div className="space-y-4">
      {gate.fetched && s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <SummaryCard label="Total visits" value={s.total_visits} sub={`${s.unique_properties} properties • whole date range`}
            icon={Footprints} testId="visits-summary-total" />
          <SummaryCard label="Supervisor visits" value={s.supervisor_visits} sub="incl. admin visits" icon={ShieldCheck}
            testId="visits-summary-supervisor" />
          <SummaryCard label="Collections" value={s.collections_count} sub="receipts issued on visits"
            icon={Users} testId="visits-summary-collections" />
          <SummaryCard label="Amount collected" value={fmtINR(s.collections_amount)} sub="on the visits above" icon={IndianRupee}
            testId="visits-summary-amount" />
        </div>
      )}

      {/* Phase 115: filters in reading order — Date From, Date To, Zone, Ward, Mohalla, Visited by, Staff, Outcome, Search, Excel */}
      <Card className="shadow-sm">
        <FetchForm onFetch={runFetch} testId="visits-filter-form">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end" data-testid="visits-filter-bar">
          <div className="space-y-1">
            <Label className="text-xs">Date From</Label>
            <Input type="date" className="w-36 h-10" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="visits-filter-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Date To</Label>
            <Input type="date" className="w-36 h-10" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="visits-filter-to" />
          </div>
          {/* Zone -> Ward -> Mohalla (cascading) */}
          <div className="basis-full md:basis-auto md:w-[480px] xl:w-[520px]" data-testid="visits-territory-filters">
            <TerritoryFilterSelects masters={territoryMasters}
              value={{ zoneId: filters.zone_id, wardId: filters.ward_id, mohallaId: filters.mohalla_id }}
              onChange={changeTerritory}
              testIdPrefix="visits-filter" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Visited By</Label>
            <Select value={filters.visited_by_role || "all"} onValueChange={changeRole}>
              <SelectTrigger className="w-40 h-10" data-testid="visits-filter-role"><SelectValue placeholder="All roles" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="visits-filter-role-all">All roles</SelectItem>
                <SelectItem value="supervisor" data-testid="visits-filter-role-supervisor">Supervisor</SelectItem>
                <SelectItem value="tax_collector" data-testid="visits-filter-role-tax_collector">Tax Collector</SelectItem>
                {isAdmin && <SelectItem value="municipal_admin" data-testid="visits-filter-role-municipal_admin">Admin</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">
              Staff
              {territoryLabel && (
                <span className="ml-1.5 font-normal text-muted-foreground" data-testid="visits-filter-staff-scope">
                  · {staffCount} in {territoryLabel}
                </span>
              )}
            </Label>
            <Select value={filters.staff_id || "all"} onValueChange={changeStaff}>
              <SelectTrigger className="w-52 h-10" data-testid="visits-filter-staff" data-staff-count={staffCount}>
                <SelectValue placeholder="All staff" />
              </SelectTrigger>
              <SelectContent data-testid="visits-filter-staff-options">
                <SelectItem value="all" data-testid="visits-filter-staff-all">
                  {filters.visited_by_role ? `All ${ROLE_LABELS[filters.visited_by_role].toLowerCase()}` : "All staff"}
                  {territoryLabel ? ` in ${territoryLabel}` : ""}
                </SelectItem>
                {staffGroups.map((g) => (
                  <SelectGroup key={g.role} data-testid={`visits-filter-staff-group-${g.role}`}>
                    {/* group headings only when several roles are listed */}
                    {!filters.visited_by_role && <SelectLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">{g.label}</SelectLabel>}
                    {g.items.map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`visits-filter-staff-option-${u.id}`} data-role={u.role}>
                        {staffOptionLabel(u)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
                {staffCount === 0 && (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground" data-testid="visits-filter-staff-empty">
                    {territoryLabel
                      ? `No ${filters.visited_by_role ? ROLE_LABELS[filters.visited_by_role].toLowerCase() : "staff"} mapped to ${territoryLabel}`
                      : "No staff for this role"}
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Outcome</Label>
            <Select value={filters.outcome || "all"}
              onValueChange={(v) => setFilters({ ...filters, outcome: v === "all" ? "" : v })}>
              <SelectTrigger className="w-52 h-10" data-testid="visits-filter-outcome"><SelectValue placeholder="Outcome" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All outcomes</SelectItem>
                {Object.entries(VISIT_OUTCOME_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[200px]">
            <Label className="text-xs">PIN or owner name</Label>
            <Input className="h-10" placeholder="Search PIN or owner name..." value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="visits-filter-search" />
          </div>
          <FetchButton type="submit" loading={loading} dirty={gate.dirty} fetched={gate.fetched} testId="visits-fetch-button" />
          {!readOnly && <Button type="button" variant="outline" className="h-10" onClick={doExport} disabled={exporting || !gate.fetched} data-testid="visits-export-button">
            <Download className="h-4 w-4 mr-1.5" /> {exporting ? "Preparing..." : "Excel"}
          </Button>}
          {(filters.zone_id || filters.ward_id || filters.mohalla_id || filters.visited_by_role || filters.staff_id || filters.outcome || filters.search) && (
            <Button type="button" variant="ghost" size="sm" className="h-10 text-muted-foreground" onClick={resetFilters}
              data-testid="visits-filter-clear">
              Clear filters
            </Button>
          )}
          <FetchDirtyHint dirty={gate.dirty} testId="visits-fetch-dirty" className="basis-full" />
        </CardContent>
        </FetchForm>
      </Card>

      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={runFetch} testId="visits-fetch-prompt"
            subtitle="Pick the date range, area, staff and outcome above, then click Fetch to run the visit report." />
        </CardContent></Card>
      ) : !data ? <Spinner /> : data.rows.length === 0 ? (
        <EmptyState title="No visits found" subtitle="Try widening the date range or clearing filters, then Fetch again" />
      ) : (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b text-xs text-muted-foreground" data-testid="visits-report-range">
              <span data-testid="visits-report-showing">
                Showing <span className="font-semibold text-foreground tabular-nums">{firstRow.toLocaleString("en-IN")}–{lastRow.toLocaleString("en-IN")}</span> of{" "}
                <span className="font-semibold text-foreground tabular-nums">{total.toLocaleString("en-IN")}</span> visits • newest first • Excel export includes every row
              </span>
              <span className="inline-flex items-center gap-1.5">
                Rows per page
                <Select value={String(pageSize)} onValueChange={(v) => changePageSize(Number(v))}>
                  <SelectTrigger className="h-7 w-20 text-xs" data-testid="visits-page-size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[50, 100, 200, 500].map((n) => <SelectItem key={n} value={String(n)} data-testid={`visits-page-size-${n}`}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </span>
            </div>
            <div className="overflow-x-auto">
              <Table data-testid="visits-report-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Visited by</TableHead>
                    <TableHead className="text-center">Visit #</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead className="hidden lg:table-cell">Remarks</TableHead>
                    <TableHead className="text-right">Collected</TableHead>
                    <TableHead className="hidden md:table-cell">Follow-up</TableHead>
                    <TableHead className="hidden lg:table-cell">Mapped collector</TableHead>
                    <TableHead className="w-20"><span className="inline-flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> GPS photo</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((v, i) => (
                    <TableRow key={v.id} data-testid={`visit-report-row-${i}`}>
                      <TableCell className="text-xs whitespace-nowrap">
                        <VisitWhen v={v} testId={`visit-report-when-${i}`} />
                      </TableCell>
                      <TableCell>
                        <button className="font-mono text-sm text-primary underline underline-offset-2"
                          onClick={() => setFocusProp(v.property_id)} data-testid={`visit-report-pin-${i}`}>{v.pin}</button>
                        <p className="text-xs text-muted-foreground">{v.owner_name || "-"} • {v.ward_label || "-"}</p>
                        {v.mohalla_label && (
                          <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid={`visit-report-mohalla-${i}`}>
                            <MapPin className="h-3 w-3" /> {v.mohalla_label}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          <StaffAvatar code={code} userId={v.visited_by_id} name={v.visited_by_name} size="sm" />
                          <span>
                            <p className="text-sm"><StaffLabel code={code} userId={v.visited_by_id} name={v.visited_by_name} empCode={v.visited_by_employee_id} /></p>
                            <RoleBadge role={v.visited_by_role} />
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="text-center text-sm tabular-nums">{v.visit_number || "-"}</TableCell>
                      <TableCell className="text-sm">{outcomeLabel(v)}</TableCell>
                      <TableCell className="hidden lg:table-cell text-xs max-w-[240px] truncate" title={v.notes || ""}>{v.notes || "-"}</TableCell>
                      <TableCell className="text-right">
                        {v.amount_collected ? (
                          <div data-testid={`visit-report-collected-${i}`}>
                            <p className="text-sm font-semibold tabular-nums">{fmtINR(v.amount_collected)}</p>
                            <p className="text-[11px] text-muted-foreground font-mono">{(v.receipt_numbers || [v.receipt_number]).filter(Boolean).join(", ")}</p>
                            {(v.transaction_id || v.payment_mode) && (
                              <p className="text-[10px] text-muted-foreground">{[v.payment_mode, v.transaction_id ? `Txn ${v.transaction_id}` : null].filter(Boolean).join(" • ")}</p>
                            )}
                          </div>
                        ) : v.receipt_deleted ? (
                          <span className="text-[11px] text-muted-foreground italic" title="The receipt behind this visit was deleted or replaced by a bulk import; it is not counted" data-testid={`visit-report-receipt-deleted-${i}`}>Receipt deleted</span>
                        ) : <span className="text-muted-foreground text-xs">-</span>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs">{v.expected_date || v.follow_up_date ? fmtDate(v.expected_date || v.follow_up_date) : "-"}</TableCell>
                      <TableCell className="hidden lg:table-cell text-xs">{v.mapped_collector_name ? <StaffName code={code} userId={v.mapped_collector_id} name={v.mapped_collector_name} size="xs" nameClassName="text-xs" /> : "-"}</TableCell>
                      <TableCell className="py-1.5"><VisitPhotoCell code={code} v={v} index={i} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {pages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t text-xs text-muted-foreground" data-testid="visits-pagination">
                <span data-testid="visits-page-info">Page <span className="font-semibold text-foreground tabular-nums">{curPage}</span> of <span className="font-semibold text-foreground tabular-nums">{pages}</span> • {total.toLocaleString("en-IN")} visits</span>
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="sm" className="h-8" disabled={curPage <= 1 || loading} onClick={() => setPage(1)} data-testid="visits-page-first">First</Button>
                  <Button variant="outline" size="sm" className="h-8" disabled={curPage <= 1 || loading} onClick={() => setPage(curPage - 1)} data-testid="visits-page-prev">Previous</Button>
                  <Input type="number" min={1} max={pages} value={curPage} aria-label="Go to page"
                    onChange={(e) => { const n = Number(e.target.value); if (n >= 1 && n <= pages) setPage(n); }}
                    className="h-8 w-16 text-center text-xs" data-testid="visits-page-input" />
                  <Button variant="outline" size="sm" className="h-8" disabled={curPage >= pages || loading} onClick={() => setPage(curPage + 1)} data-testid="visits-page-next">Next</Button>
                  <Button variant="outline" size="sm" className="h-8" disabled={curPage >= pages || loading} onClick={() => setPage(pages)} data-testid="visits-page-last">Last</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <PropertyDetailSheet code={code} propId={focusProp} onClose={() => setFocusProp(null)} />
    </div>
  );
}

function AssignmentsTab({ code, readOnly = false }) {
  const [supervisors, setSupervisors] = useState([]);
  const [supId, setSupId] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [assignments, setAssignments] = useState(null);

  useEffect(() => {
    api.get(`/t/${code}/users`).then((r) => {
      setSupervisors((r.data || []).filter((u) => u.role === "supervisor" && u.is_active));
    }).catch(() => {});
  }, [code]);

  const load = useCallback(() => api.get(`/t/${code}/visits/supervisor-assignments`)
    .then((r) => setAssignments(r.data)).catch(() => setAssignments([])), [code]);
  useEffect(() => { load(); }, [load]);

  const assign = async (e) => {
    e?.preventDefault();
    if (!supId) return toast.error("Select the supervisor first");
    const v = pin.trim();
    if (!v) return toast.error("Enter the property PIN");
    setBusy(true);
    try {
      const lk = await api.get(`/t/${code}/properties/pin-lookup`, { params: { pin: v } });
      const r = await api.post(`/t/${code}/visits/supervisor-assignments`, {
        supervisor_id: supId, property_ids: [lk.data.id],
      });
      if (r.data.created > 0) {
        toast.success(`${lk.data.pin} assigned — the supervisor has been notified`);
      } else {
        toast.info("This property is already assigned to that supervisor");
      }
      setPin("");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/t/${code}/visits/supervisor-assignments/${a.id}`);
      toast.success(`${a.pin} removed from ${a.supervisor_name}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-4">
      {readOnly && <ReadOnlyNotice what="supervisor assignments" testId="supervisor-assignments-readonly-notice" />}
      {!readOnly && <Card className="shadow-sm">
        <CardContent className="p-4">
          <p className="font-medium text-sm mb-3">Assign a property to a supervisor for field visits</p>
          <form onSubmit={assign} className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Supervisor</Label>
              <Select value={supId} onValueChange={setSupId}>
                <SelectTrigger className="w-56" data-testid="assign-supervisor-select"><SelectValue placeholder="Select supervisor" /></SelectTrigger>
                <SelectContent>
                  {supervisors.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`assign-supervisor-option-${u.id}`}>{staffLabelText(u.name, u.employee_id)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Property PIN</Label>
              <Input className="w-48 font-mono" placeholder="e.g. BMC100041" value={pin}
                onChange={(e) => setPin(e.target.value)} data-testid="assign-pin-input" />
            </div>
            <Button type="submit" disabled={busy} data-testid="assign-submit-button">
              <Plus className="h-4 w-4 mr-1.5" /> {busy ? "Assigning..." : "Assign"}
            </Button>
          </form>
        </CardContent>
      </Card>}

      {!assignments ? <Spinner /> : assignments.length === 0 ? (
        <EmptyState title="No supervisor assignments yet" subtitle="Assign priority properties so supervisors can visit and collect directly" />
      ) : (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <Table data-testid="supervisor-assignments-table">
              <TableHeader>
                <TableRow>
                  <TableHead>PIN</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Supervisor</TableHead>
                  <TableHead>Assigned by</TableHead>
                  <TableHead>Date</TableHead>
                  {!readOnly && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.map((a, i) => (
                  <TableRow key={a.id} data-testid={`supervisor-assignment-row-${i}`}>
                    <TableCell className="font-mono text-sm">{a.pin}</TableCell>
                    <TableCell className="text-sm">{a.owner_name || "-"}</TableCell>
                    <TableCell className="text-sm">
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-[11px]"><StaffLabel code={code} userId={a.supervisor_id} name={a.supervisor_name} codeClassName="text-amber-800/80" /></Badge>
                    </TableCell>
                    <TableCell className="text-sm"><StaffLabel code={code} userId={a.assigned_by} name={a.assigned_by_name} /></TableCell>
                    <TableCell className="text-xs">{fmtDate((a.created_at || "").slice(0, 10))}</TableCell>
                    {!readOnly && <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                        onClick={() => remove(a)} data-testid={`assignment-remove-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Phase 152: admin Visit Reports tabs <-> "visits" module sub-features
const ADMIN_TABS = [
  { value: "report", feature: "report" },
  { value: "overrides", feature: "report" },   // Phase 217: rides on the Visit Report feature
  { value: "assignments", feature: "supervisor_assignments" },
];


/** Phase 217 — "Same-day visit overrides": every time a TC / Supervisor continued on a property that another staff
 *  member had already visited that day. Date, PIN, first visitor, who continued (role), time, what they did. */
function SameDayOverridesTab({ code, onCount }) {
  const [filters, setFilters] = useState({ date_from: daysAgoISO(30), date_to: todayISO(), search: "", zone_id: "", ward_id: "", mohalla_id: "" });
  const territoryMasters = useTerritoryMasters(code);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [focusProp, setFocusProp] = useState(null);
  const gate = useFetchGate(filters);
  const params = useMemo(() => {
    const p = { page, limit: 50 };
    Object.entries(gate.applied || {}).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [gate.appliedKey, page]); // eslint-disable-line react-hooks/exhaustive-deps
  const reqSeq = React.useRef(0);
  const load = useCallback(() => {
    const seq = ++reqSeq.current;
    setLoading(true);
    return api.get(`/t/${code}/visits/same-day-overrides`, { params })
      .then((r) => {
        if (seq !== reqSeq.current) return;
        setData(r.data);
        // the tab badge follows the range the admin actually fetched (date range + area + search)
        onCount?.(r.data?.total ?? 0, { from: params.date_from, to: params.date_to });
      })
      .catch((e) => { if (seq === reqSeq.current) toast.error(errMsg(e)); })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  }, [code, params, onCount]);
  useEffect(() => { if (gate.fetched) load(); }, [load, gate.fetched, gate.version]);
  useEffect(() => { setPage(1); }, [gate.appliedKey]);
  const s = data?.summary;
  const rows = data?.rows || [];
  const pages = data?.pages || 1;
  return (
    <div className="space-y-4" data-testid="same-day-overrides-tab">
      <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="same-day-overrides-intro">
        <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          Listed here: a Tax Collector or Supervisor opened a property that was already visited today by someone else, saw the warning and continued.
          A visit-only continuation is <b>not counted</b> as a visit in their name (it stays with the first visitor); a payment <b>is counted</b> as their own visit.
        </span>
      </div>
      {gate.fetched && s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <SummaryCard label="Overrides" value={s.total} sub="continued after the warning" icon={ShieldAlert} testId="overrides-summary-total" />
          <SummaryCard label="Visited only" value={s.visit_only} sub="not counted in their name" icon={Footprints} testId="overrides-summary-visit-only" />
          <SummaryCard label="Collected a payment" value={s.payments} sub="counted as their visit" icon={Users} testId="overrides-summary-payments" />
          <SummaryCard label="Amount collected" value={fmtINR(s.amount_collected)} icon={IndianRupee} testId="overrides-summary-amount" />
        </div>
      )}
      <Card className="shadow-sm">
        <FetchForm onFetch={gate.fetch} testId="overrides-filter-form">
          <CardContent className="p-4 flex flex-wrap gap-3 items-end" data-testid="overrides-filter-bar">
            <div className="space-y-1">
              <Label className="text-xs">Date From</Label>
              <Input type="date" className="w-36 h-10" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="overrides-filter-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date To</Label>
              <Input type="date" className="w-36 h-10" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="overrides-filter-to" />
            </div>
            <div className="basis-full md:basis-auto md:w-[480px] xl:w-[520px]">
              <TerritoryFilterSelects masters={territoryMasters}
                value={{ zoneId: filters.zone_id, wardId: filters.ward_id, mohallaId: filters.mohalla_id }}
                onChange={(tv) => setFilters({ ...filters, zone_id: tv.zoneId, ward_id: tv.wardId, mohalla_id: tv.mohallaId })}
                testIdPrefix="overrides-filter" />
            </div>
            <div className="space-y-1 flex-1 min-w-[200px]">
              <Label className="text-xs">PIN, owner or staff name</Label>
              <Input className="h-10" placeholder="Search PIN, owner or staff name..." value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="overrides-filter-search" />
            </div>
            <FetchButton type="submit" loading={loading} dirty={gate.dirty} fetched={gate.fetched} testId="overrides-fetch-button" />
            <FetchDirtyHint dirty={gate.dirty} testId="overrides-fetch-dirty" className="basis-full" />
          </CardContent>
        </FetchForm>
      </Card>
      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="overrides-fetch-prompt" subtitle="Pick the date range and area above, then click Fetch to list the same-day visit overrides." />
        </CardContent></Card>
      ) : !data ? <Spinner /> : rows.length === 0 ? (
        <EmptyState title="No same-day visit overrides" subtitle="Nobody continued on an already-visited property in this period." />
      ) : (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="same-day-overrides-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Property PIN</TableHead>
                    <TableHead>First visitor</TableHead>
                    <TableHead>Continued after warning</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>What they did</TableHead>
                    <TableHead className="text-center">Counted?</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={r.id} data-testid={`override-row-${i}`} data-action={r.action}>
                      <TableCell className="text-xs whitespace-nowrap tabular-nums" data-testid={`override-date-${i}`}>{fmtDate(r.visit_date_ist)}</TableCell>
                      <TableCell>
                        <button className="font-mono text-sm text-primary underline underline-offset-2" onClick={() => setFocusProp(r.property_id)} data-testid={`override-pin-${i}`}>{r.pin}</button>
                        <p className="text-xs text-muted-foreground">{r.owner_name || "-"}{r.ward_label ? ` • ${r.ward_label}` : ""}</p>
                        {r.mohalla_label && <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.mohalla_label}</p>}
                      </TableCell>
                      <TableCell data-testid={`override-first-${i}`}>
                        <span className="flex items-center gap-2">
                          <StaffAvatar code={code} userId={r.first_visitor_id} name={r.first_visitor_name} size="sm" />
                          <span>
                            <p className="text-sm"><StaffLabel code={code} userId={r.first_visitor_id} name={r.first_visitor_name} empCode={r.first_visitor_employee_id} /></p>
                            <span className="flex items-center gap-1.5 flex-wrap">
                              <RoleBadge role={r.first_visitor_role} />
                              <span className="text-[11px] text-muted-foreground">{r.first_visit_time_ist ? `${r.first_visit_time_ist} IST` : ""}{r.first_visit_imported ? " • imported payment" : ""}</span>
                              {r.first_visit_receipt_deleted && <ReceiptDeletedBadge v={{ deleted_receipt: { receipt_number: r.first_visit_receipt, amount: r.first_visit_amount } }} testId={`override-first-receipt-deleted-${i}`} />}
                            </span>
                          </span>
                        </span>
                      </TableCell>
                      <TableCell data-testid={`override-continued-${i}`}>
                        <span className="flex items-center gap-2">
                          <StaffAvatar code={code} userId={r.continued_by_id} name={r.continued_by_name} size="sm" />
                          <span>
                            <p className="text-sm"><StaffLabel code={code} userId={r.continued_by_id} name={r.continued_by_name} empCode={r.continued_by_employee_id} /></p>
                            <RoleBadge role={r.continued_by_role} />
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap tabular-nums" data-testid={`override-time-${i}`}>{r.continued_time_ist ? `${r.continued_time_ist} IST` : fmtDateTime(r.continued_at)}</TableCell>
                      <TableCell className="text-xs" data-testid={`override-action-${i}`}>
                        {r.action === "payment" && r.receipt_deleted ? (
                          <>
                            <p className="font-medium text-rose-700 line-through decoration-rose-400">Collected {fmtINR(r.amount_collected)}</p>
                            <ReceiptDeletedBadge v={{ deleted_receipt: { receipt_number: r.receipt_number, amount: r.amount_collected } }} testId={`override-receipt-deleted-${i}`} />
                          </>
                        ) : r.action === "payment" ? (
                          <>
                            <p className="font-medium text-emerald-800">Collected {fmtINR(r.amount_collected)}</p>
                            <p className="text-muted-foreground font-mono">{r.receipt_number}</p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium">Visited only</p>
                            <p className="text-muted-foreground truncate max-w-[220px]" title={r.notes || ""}>{r.outcome_label || r.outcome}{r.notes ? ` — ${r.notes}` : ""}</p>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-center" data-testid={`override-counted-${i}`}>
                        {r.counted ? (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">Counted — their visit</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">Not counted — stays with {r.first_visitor_name?.split(" ")[0] || "first visitor"}</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {pages > 1 && (
              <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-muted-foreground" data-testid="overrides-pagination">
                <span>Page {data.page} of {pages} • {data.total} overrides</span>
                <span className="flex gap-2">
                  <Button variant="outline" size="sm" className="h-8" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} data-testid="overrides-prev">Previous</Button>
                  <Button variant="outline" size="sm" className="h-8" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)} data-testid="overrides-next">Next</Button>
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <PropertyDetailSheet code={code} propId={focusProp} onClose={() => setFocusProp(null)} />
    </div>
  );
}

export default function VisitReports() {
  const { code } = useParams();
  const { auth } = useAuth();
  const isAdmin = auth?.user?.role === "municipal_admin";
  const access = useRoleModules(code);
  // supervisors use the "visits.visit_reports" sub-feature (Phase 152) — admins the "visits.report" / "visits.supervisor_assignments" pair
  const supRO = !isAdmin && !access.canWrite("visits", "visit_reports");
  const show = (f) => access.featureAllowed("visits", f);
  const ro = (f) => !access.canWrite("visits", f);
  const visibleTabs = ADMIN_TABS.filter((t) => show(t.feature));
  const [tab, setTab] = useState("report");
  // Phase 217: count badge on the "Same-day visit overrides" tab — last 30 days until the admin fetches a range of their own
  const [overrideCount, setOverrideCount] = useState(null);
  const onOverrideCount = useCallback((total, range) => setOverrideCount({ total, ...(range || {}) }), []);
  const canSeeOverrides = isAdmin && access.loaded && show("report");
  useEffect(() => {
    if (!canSeeOverrides) return;
    let alive = true;
    const from = daysAgoISO(30), to = todayISO();
    api.get(`/t/${code}/visits/same-day-overrides`, { params: { date_from: from, date_to: to, limit: 1 } })
      .then((r) => { if (alive) setOverrideCount((c) => c ?? { total: r.data?.total ?? 0, from, to }); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, canSeeOverrides]);
  const overrideBadgeTitle = overrideCount
    ? `${overrideCount.total} same-day visit override${overrideCount.total === 1 ? "" : "s"}` +
      (overrideCount.from && overrideCount.to ? ` between ${fmtDate(overrideCount.from)} and ${fmtDate(overrideCount.to)}` : "")
    : undefined;
  useEffect(() => {
    if (!access.loaded || !isAdmin) return;
    if (!visibleTabs.some((t) => t.value === tab)) { const f = firstAllowedTab(access, "visits", ADMIN_TABS); if (f) setTab(f); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.loaded, visibleTabs.length, isAdmin]);

  return (
    <div>
      <PageHeader title="Visit Reports"
        subtitle="Property-wise visit and collection history across your team, with outcomes and follow-ups" />
      {isAdmin ? (
        access.loaded && visibleTabs.length === 0 ? <NoAccessPanel what="Visit Reports" testId="visits-no-access" /> : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            {show("report") && <TabsTrigger value="report" data-testid="visits-tab-report"><Filter className="h-3.5 w-3.5 mr-1.5" /> Visit Report{ro("report") && <ViewOnlyBadge />}</TabsTrigger>}
            {show("report") && (
              <TabsTrigger value="overrides" data-testid="visits-tab-overrides" title={overrideBadgeTitle}>
                <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> Same-day visit overrides
                {overrideCount && overrideCount.total > 0 && (
                  <Badge variant="outline" className="ml-1.5 h-5 min-w-5 px-1.5 text-[10px] tabular-nums justify-center bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-200 dark:border-amber-900"
                    data-testid="visits-tab-overrides-count" aria-label={overrideBadgeTitle}>
                    {overrideCount.total > 999 ? "999+" : overrideCount.total}
                  </Badge>
                )}
              </TabsTrigger>
            )}
            {show("supervisor_assignments") && <TabsTrigger value="assignments" data-testid="visits-tab-assignments"><Users className="h-3.5 w-3.5 mr-1.5" /> Supervisor Assignments{ro("supervisor_assignments") && <ViewOnlyBadge />}</TabsTrigger>}
          </TabsList>
          <TabsContent value="report"><ReportTab code={code} isAdmin readOnly={ro("report")} /></TabsContent>
          <TabsContent value="overrides"><SameDayOverridesTab code={code} onCount={onOverrideCount} /></TabsContent>
          <TabsContent value="assignments"><AssignmentsTab code={code} readOnly={ro("supervisor_assignments")} /></TabsContent>
        </Tabs>
        )
      ) : (
        <ReportTab code={code} isAdmin={false} readOnly={supRO} />
      )}
    </div>
  );
}
